import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { LatinizeService } from '../../common/latinize.service';
import { WindowRef } from '../../common/window-ref';
import { ConnectedOverlayDirective } from '../../core/overlay/overlay-directives';
import { ScrollableDirective } from '../../core/overlay/scroll/scrollable.directive';
import { RtPlatform } from '../../core/platform/platform';
import { RtStatefulComponent } from '../../utils/rt-stateful-component';

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_ESCAPE = 27;
const KEY_LEFT = 37;
const KEY_UP = 38;
const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_2 = 50;

export type State = {
  dropdownOpen: boolean;
  valueHighlight: string;
  searchList: any;
  searchItemIndex: number;
  componentWidth: number;
};

@Component({
  selector: 'rt-mentions-input',
  templateUrl: './mentions-input.component.html',
  styleUrls: ['./mentions-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-plain]': 'isPlain',
    '[class.has-red-placeholder]': 'hasRedPlaceholder',
  },
})
export class MentionsInputComponent extends RtStatefulComponent<State> {
  public readonly TEXTAREA_MIN_HEIGHT: number = 60;

  @ViewChild(ConnectedOverlayDirective, { static: true }) public searchListOverlay: any;
  @ViewChild('textarea', { static: true }) public textarea: ElementRef;
  @ViewChild('mentionsHighlight') public mentionsHighlight: ElementRef;

  @Input()
  public set admins(admins: any[]) {
    this.sortedAdmins = [...(Array.isArray(admins) ? admins : [])]
      .sort((a, b) => a.firstName.localeCompare(b.firstName))
      .map(admin => {
        const name = `${admin.firstName.toLowerCase()} ${admin.lastName.toLowerCase()}`;
        return {
          ...admin,
          name,
          latinizedName: this.latinizeService.getLatinized(name),
        };
      });
  }

  public get admins(): any[] {
    return this.sortedAdmins;
  }

  @Input() public placeholder: string = '';
  @Input() public value: string = '';
  @Input() public readOnly: boolean = false;
  @Input() public isPlain: boolean = false;
  @Input() public autoFocus: boolean = false;
  @Input() public hasRedPlaceholder: boolean = false;
  @Input() public hasProtectedControl: boolean = true;

  @Output() public focusChange: EventEmitter<boolean> = new EventEmitter();
  @Output() public valueChange: EventEmitter<any> = new EventEmitter();

  public formControl: FormControl = new FormControl(null);

  private sortedAdmins: any[];
  private ngOnDestroy$: Subject<void> = new Subject();
  private inputElement: any;
  private startPos: number;
  private isSearching: boolean = false;
  private mentionedAdmins: any[] = [];
  private valueSyntax: string = '';
  private scrollContainer: HTMLElement | null;
  private scrollPosition: number;
  private textareaHeight: number;

  constructor(
    public element: ElementRef,
    private platform: RtPlatform,
    private ngZone: NgZone,
    private windowRef: WindowRef,
    private latinizeService: LatinizeService,
    protected cdRef: ChangeDetectorRef,
    @Optional() private scrollable: ScrollableDirective,
  ) {
    super(cdRef);
    this.createState({
      dropdownOpen: false,
      valueHighlight: '',
      searchList: [],
      searchItemIndex: 0,
      componentWidth: 0,
    });
  }

  public ngOnInit(): void {
    const destroy$ = this.ngOnDestroy$.asObservable();

    this.scrollContainer = document.querySelector('.rt-overlay-container rt-scrollable');
    this.inputElement = this.textarea.nativeElement;
    this.formControl.valueChanges.pipe(takeUntil(destroy$)).subscribe(value => {
      this.updateValue(value);
      this.adjustInputHeight();
    });
    this.adjustInputHeight();
    this.updateValue(this.value, { emitEvent: false });

    this.searchListOverlay.positions = [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    ];

    this.ngZone.runOutsideAngular(() => {
      observableFromEvent(this.inputElement, 'keydown')
        .pipe(takeUntil(destroy$))
        .subscribe((evt: Event) => {
          this.onKeyDown(evt);
        });

      observableFromEvent(this.inputElement, 'mousedown')
        .pipe(takeUntil(destroy$))
        .subscribe(() => {
          this.onMousedown();
        });

      observableFromEvent(this.inputElement, 'blur')
        .pipe(takeUntil(destroy$))
        .subscribe((evt: Event) => {
          this.onBlur(evt);
        });

      observableFromEvent(this.inputElement, 'focus')
        .pipe(takeUntil(destroy$))
        .subscribe(() => {
          this.onFocus();
        });

      observableFromEvent(this.windowRef.nativeWindow, 'resize')
        .pipe(debounceTime(50), takeUntil(destroy$))
        .subscribe(() => {
          this.updateComponentWidth();
        });
    });
  }

  public ngAfterContentInit(): void {
    setTimeout(() => {
      this.adjustInputHeight();
      this.updateValue(this.formControl.value);
      // @ts-ignore
      if (!this.cdRef['destroyed']) {
        this.cdRef.detectChanges();
      }
    }, 0);
  }

  public ngAfterViewInit(): void {
    this.updateComponentWidth();
  }

  public ngOnChanges(changes: any): void {
    if (changes.value) {
      if (changes.value.firstChange || !changes.value.previousValue) {
        this.mentionedAdmins = this.extractMentionedAdmins(changes.value.currentValue);
      }
      if (changes.value.currentValue !== this.formControl.value) {
        this.formControl.setValue(changes.value.currentValue);
      }
    }

    if (changes.autoFocus && changes.autoFocus.currentValue) {
      this.setFocus();
    }
  }

  public ngOnDestroy(): void {
    this.scrollContainer = null;
    this.inputElement = null;
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public updateComponentWidth(): void {
    this.setState({
      ...this.state,
      componentWidth: this.getComponentWidth(),
    });
  }

  public selectItem(index: number, event: MouseEvent | null = null): void {
    // prevents unfocus of textarea
    if (event) {
      event.preventDefault();
      this.onMousedown();
    }
    const admin = this.state.searchList[index];
    if (admin) {
      if (!this.mentionedAdmins.includes(admin)) {
        this.mentionedAdmins.push(admin);
      }

      const name = `${admin.firstName} ${admin.lastName} `;
      const val = this.inputElement.value;
      const pos = val.slice(0, this.inputElement.selectionStart).length;
      const caretPosition = this.startPos + name.length;
      this.formControl.setValue(
        val.substring(0, this.startPos) + name + val.substring(pos, val.length),
      );
      this.inputElement.focus();
      this.inputElement.setSelectionRange(caretPosition, caretPosition);
    }
  }

  public nextSearchItem(): void {
    this.setState({
      ...this.state,
      searchItemIndex: Math.max(
        Math.min(this.state.searchItemIndex + 1, this.state.searchList.length - 1),
        0,
      ),
    });
  }

  public previousSearchItem(): void {
    this.setState({
      ...this.state,
      searchItemIndex: Math.max(
        Math.min(this.state.searchItemIndex - 1, this.state.searchList.length - 1),
        0,
      ),
    });
  }

  public onFocus(): void {
    this.focusChange.emit(true);
  }

  public onBlur(event: Event): void {
    this.stopEvent(event);
    this.isSearching = false;
    this.focusChange.emit(false);
    this.closeDropdown();
  }

  public onMousedown(): void {
    this.isSearching = false;
    this.closeDropdown();
  }

  public onKeyDown(event: any): boolean | void {
    const val = this.inputElement && this.inputElement.value;
    let pos = val.slice(0, this.inputElement.selectionStart).length;
    let charPressed = event.key;
    if (!charPressed) {
      const charCode = event.which || event.keyCode;
      if (!event.shiftKey && charCode >= 65 && charCode <= 90) {
        charPressed = String.fromCharCode(charCode + 32);
      } else if (event.shiftKey && charCode === KEY_2) {
        charPressed = '@';
      } else {
        charPressed = String.fromCharCode(event.which || event.keyCode);
      }
    }
    if (event.keyCode === KEY_ENTER && event.wasClick && pos < this.startPos) {
      pos = this.inputElement.length;
      this.inputElement.focus();
      this.inputElement.setSelectionRange(pos, pos);
    }
    if (charPressed === '@') {
      this.startPos = pos;
      this.isSearching = true;
      this.setState({ ...this.state, searchList: this.filterAdmins() });
      this.openDropdown();
    } else if (
      this.startPos >= 0 &&
      !event.shiftKey &&
      !event.metaKey &&
      // !event.altKey &&
      !event.ctrlKey &&
      pos > this.startPos
    ) {
      if (this.isSearching && this.state.searchList.length > 0) {
        if (this.state.searchList.length < this.state.searchItemIndex) {
          this.setState({ ...this.state, searchItemIndex: 0 });
        }

        if (event.keyCode === KEY_TAB || event.keyCode === KEY_ENTER) {
          this.stopEvent(event);
          this.isSearching = false;
          this.selectItem(this.state.searchItemIndex);
          this.closeDropdown();
          this.startPos = -1;
          return false;
        } else if (event.keyCode === KEY_ESCAPE) {
          this.stopEvent(event);
          this.isSearching = false;
          this.closeDropdown();
          return false;
        } else if (event.keyCode === KEY_DOWN) {
          this.stopEvent(event);
          this.nextSearchItem();
          return false;
        } else if (event.keyCode === KEY_UP) {
          this.stopEvent(event);
          this.previousSearchItem();
          return false;
        } else if (event.keyCode === KEY_LEFT || event.keyCode === KEY_RIGHT) {
          this.stopEvent(event);
          return false;
        }
      }
      let mention = val.substring(this.startPos, pos);
      if (event.keyCode === KEY_BACKSPACE) {
        mention = mention.slice(0, -1);
        if (mention && mention.length) {
          this.isSearching = true;
          this.openDropdown();
        }
      } else if (charPressed.length === 1) {
        mention += charPressed;
      } else {
        return false;
      }

      this.setState({ ...this.state, searchList: this.filterAdmins(mention) });
      this.isSearching =
        mention.startsWith('@') && !(this.state.searchList.length === 0 || pos <= this.startPos);

      if (!this.isSearching) {
        this.closeDropdown();
      }
    }
  }

  public openDropdown(): void {
    this.setState({ ...this.state, dropdownOpen: true });
  }

  public closeDropdown(): void {
    this.setState({ ...this.state, dropdownOpen: false });
  }

  public trackByAdmin(index: number, admin: any): number {
    return admin.id;
  }

  private setFocus(): void {
    this.textarea.nativeElement.focus();
  }

  private getComponentWidth(): number {
    return this.element.nativeElement.getBoundingClientRect().width;
  }

  private extractMentionedAdmins(value: string): any[] {
    const adminSyntaxRegExp = /admin:(\d+)/gm;

    if (!adminSyntaxRegExp.test(value) || !Array.isArray(this.admins)) {
      return [];
    }

    return value
      .match(adminSyntaxRegExp)!
      .map(admin => parseInt(admin.substr(6), 10))
      .map(adminId => this.admins.find(admin => admin.id === adminId))
      .filter(admin => !!admin);
  }

  private updateValue(newValue: string, { emitEvent = true }: { emitEvent?: boolean } = {}): void {
    let value = !newValue || !newValue.indexOf ? '' : newValue;

    if (value.indexOf('@[') !== -1) {
      value = value.replace(/@\[([^\]]+)\]\(admin:\d+\)/g, '$1');
    }

    this.valueSyntax = value;
    let valueHighlight = value;

    if (this.mentionedAdmins.length) {
      this.mentionedAdmins.forEach(admin => {
        const name = `${admin.firstName} ${admin.lastName}`;
        const nameRegex = this.regexEncode(name);

        valueHighlight = valueHighlight.replace(
          nameRegex,
          `<span class="mention"><span>${name}</span></span>`,
        );
        this.valueSyntax = this.valueSyntax.replace(nameRegex, `@[${name}](admin:${admin.id})`);
      });
    }

    this.setState({
      ...this.state,
      valueHighlight: valueHighlight.replace(/\n/g, '<br />&#8203;').replace(/ {2}/g, '&nbsp; '),
    });

    if (emitEvent) {
      this.valueChange.emit({ value: value, annotatedValue: this.valueSyntax });
    }
  }

  private regexEncode(text: string): RegExp {
    return new RegExp(`(?!\\[)${text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1')}(?!\\])`, 'gi');
  }

  private filterAdmins(mention: any = null): any {
    let admins = this.admins;
    if (mention && mention.length > 1) {
      const query = mention.substring(1).toLowerCase();
      admins = this.admins.filter(
        admin => admin.name.indexOf(query) !== -1 || admin.latinizedName.indexOf(query) !== -1,
      );
    }
    return admins.slice(0, 20);
  }

  private stopEvent(event: any): void {
    if (!event.wasClick) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  private adjustInputHeight(): void {
    if (this.mentionsHighlight) {
      const nextHeight = Math.max(
        this.mentionsHighlight.nativeElement.offsetHeight,
        this.TEXTAREA_MIN_HEIGHT,
      );

      if (this.textareaHeight !== nextHeight && this.inputElement) {
        this.inputElement.style.overflow = 'hidden';
        this.inputElement.style.height = `${nextHeight}px`;
      }
    } else if (this.inputElement) {
      this.inputElement.style.height = `${this.TEXTAREA_MIN_HEIGHT}px`;
    }

    if (this.searchListOverlay && this.searchListOverlay.overlayRef) {
      this.searchListOverlay.overlayRef.updatePosition();
    }
  }
}
